import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when window is resized to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen ? 'bg-white shadow-lg' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo */}
          <motion.div
            className="flex items-center cursor-pointer"
            onClick={() => scrollToSection('hero')}
            whileTap={{ scale: 0.95 }}
          >
            <img
              src="/images/logo.png"
              alt="MP Frankendach Logo"
              className="h-10 sm:h-12 w-auto object-contain"
            />
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
            <NavLink onClick={() => scrollToSection('hero')}>Startseite</NavLink>
            <NavLink onClick={() => scrollToSection('leistungen')}>Leistungen</NavLink>
            <NavLink onClick={() => scrollToSection('referenzen')}>Referenzen</NavLink>
            <NavLink onClick={() => scrollToSection('kontakt')}>Kontakt</NavLink>
          </nav>

          {/* Phone Number - Desktop */}
          <motion.a
            href="tel:+4991198012850"
            className="hidden md:flex items-center space-x-2 bg-gradient-to-r from-red-600 to-red-800 text-white px-4 lg:px-6 py-2.5 lg:py-3 rounded-lg font-semibold text-sm lg:text-base hover:shadow-lg transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Phone className="w-4 h-4" />
            <span className="hidden lg:inline">0911 - 980 12 850</span>
            <span className="lg:hidden">Anrufen</span>
          </motion.a>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-900 p-2 -mr-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              <MobileNavLink onClick={() => scrollToSection('hero')}>Startseite</MobileNavLink>
              <MobileNavLink onClick={() => scrollToSection('leistungen')}>Leistungen</MobileNavLink>
              <MobileNavLink onClick={() => scrollToSection('referenzen')}>Referenzen</MobileNavLink>
              <MobileNavLink onClick={() => scrollToSection('kontakt')}>Kontakt</MobileNavLink>
              
              <div className="pt-3">
                <a
                  href="tel:+4991198012850"
                  className="flex items-center justify-center space-x-2 bg-gradient-to-r from-red-600 to-red-800 text-white px-6 py-3 rounded-lg font-semibold w-full"
                >
                  <Phone className="w-4 h-4" />
                  <span>0911 - 980 12 850</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

const NavLink = ({ children, onClick }) => (
  <motion.button
    onClick={onClick}
    className="text-gray-900 font-medium hover:text-red-600 transition-colors relative group"
    whileHover={{ y: -2 }}
  >
    {children}
    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-red-600 to-red-800 group-hover:w-full transition-all duration-300"></span>
  </motion.button>
);

const MobileNavLink = ({ children, onClick }) => (
  <button
    onClick={onClick}
    className="block text-gray-900 font-medium hover:text-red-600 hover:bg-gray-50 transition-colors py-3 px-3 w-full text-left rounded-lg"
  >
    {children}
  </button>
);
